import { Button } from '@toggle/design-system';
import { Entity } from '@toggle/toggle';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useBlocker } from 'react-router-dom';

import { fetchMultipleEntities } from '~/api/entities/entity-service';
import { resolveEntitiesFromCsv } from '~/stores/use-watchlist/entityResolver';
import { useWatchlist } from '~/stores/use-watchlist/useWatchlist';
import { WatchlistError } from '~/stores/use-watchlist/watchlist-types';
import { WatchlistTableModal } from '~/views/watchlist-page/watchlist-table-modal/WatchlistTableModal';

import { ChatWatchlistManager } from '../chat-watchlist-manager-widget/ChatWatchlistManagerWidget';
import { TableWatchlistManager } from '../table-watchlist-manager-widget/TableWatchlistManagerWidget';
import { WatchlistManagerHeader } from './components/watchlist-manager-header/WatchlistManagerHeader';
import { handleReOrderList } from './utils';
import * as S from './WatchlistManagetWidget.styles';

export interface WatchlistManagerWidgetProps {
  onListCreated: (id: string) => void;
  onCancel?: () => void;
  inModal?: boolean;
  defaultEntities?: Entity[];
  defaultName?: string;
  className?: string;
}

export const WatchlistManagerWidget = ({
  className,
  defaultEntities = [],
  inModal = true,
  defaultName = '',
  onListCreated,
  onCancel,
}: WatchlistManagerWidgetProps) => {
  const { t } = useTranslation(['widget']);
  const {
    activeListId,
    error: watchlistError,
    createList,
    updateList,
    hasListWithName,
  } = useWatchlist(state => ({
    activeListId: state.activeListId,
    error: state.error,
    createList: state.createList,
    updateList: state.updateList,
    hasListWithName: state.hasListWithName,
  }));
  const [entitiesList, setEntitiesList] = useState<Entity[]>(defaultEntities);
  const [countEntitiesAdded, setCountEntitiesAdded] = useState(0);
  const [isPending, setIsPending] = useState(false);
  const [isRenameActive, setIsRenameActive] = useState(false);
  const [listName, setListName] = useState(defaultName);
  const [errorKey, setErrorKey] = useState('');

  useEffect(() => {
    if (watchlistError) {
      setErrorKey(WatchlistError.ServerError);
    }
  }, [watchlistError]);

  const onUpdateName = (name: string) => {
    setListName(name);

    if (name) {
      setErrorKey('');
    }
  };

  const onSelectItem = (entity: Entity, isInWatchlist: boolean) => {
    if (isInWatchlist) {
      removeEntity(entity);
      setErrorKey('');
    } else {
      setCountEntitiesAdded(1);
      setEntitiesList([entity, ...entitiesList]);
      setTimeout(() => setCountEntitiesAdded(0), 300);
    }
  };

  const onChatSubmit = async (value: string) => {
    setIsPending(true);
    const tags = await resolveEntitiesFromCsv(value);

    const newTags = tags.filter(tag => !entitiesList.some(e => e.tag === tag));
    const newEntities = await fetchMultipleEntities(newTags);

    setCountEntitiesAdded(newEntities.length);
    setEntitiesList([...newEntities, ...entitiesList]);
    setErrorKey('');
    setIsPending(false);
    setTimeout(() => setCountEntitiesAdded(0), 1000);
  };

  const onConfirmSuccess = (id: string) => {
    setListName('');
    setEntitiesList([]);
    setTimeout(() => onListCreated(id), 4);
  };

  const onConfirm = async (e?: React.MouseEvent<HTMLButtonElement>) => {
    e?.stopPropagation();

    switch (true) {
      case !listName:
        setErrorKey(WatchlistError.EmptyName);
        setIsRenameActive(true);
        return false;
      case !entitiesList.length:
        setErrorKey(WatchlistError.EmptyList);
        return false;
      case hasListWithName(listName):
        setErrorKey(WatchlistError.Duplicate);
        return false;
      case activeListId && !!defaultEntities.length:
        const resUpdate = await updateList({
          id: activeListId,
          name: listName,
          tags: entitiesList.map(e => e.tag),
        });

        if (resUpdate.data) {
          onConfirmSuccess(resUpdate.data.id);
        }

        return false;

      default:
        const listId = await createList(
          listName,
          entitiesList.map(e => e.tag)
        );

        if (listId) {
          onConfirmSuccess(listId);
          return true;
        } else {
          return false;
        }
    }
  };

  const onRowOrderChange = ({
    toIdx,
    fromIdx,
  }: {
    toIdx: number;
    fromIdx: number;
  }) => {
    const newList = handleReOrderList({
      list: entitiesList,
      toIdx,
      fromIdx,
    });
    setEntitiesList(newList);
  };

  const removeEntity = (entity: Entity) => {
    setEntitiesList(entitiesList.filter(d => d.tag !== entity.tag));
  };

  const getIsInWatchlist = (item: Entity) => {
    return entitiesList.some(d => d.tag === item.tag);
  };

  const saveAndExit = async () => {
    const succeed = await onConfirm();

    if (succeed) {
      blocker.proceed?.();
    } else {
      blocker.reset?.();
    }
  };

  const hasUnsavedChanges = () => !!listName || !!entitiesList.length;

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      currentLocation.pathname !== nextLocation.pathname &&
      !inModal &&
      hasUnsavedChanges()
  );

  return (
    <S.Container
      className={className}
      $inModal={inModal}
      data-testid="watchlist-manager-widget"
    >
      <WatchlistManagerHeader
        listName={listName}
        onUpdateName={onUpdateName}
        isRenameActive={isRenameActive}
        setIsRenameActive={setIsRenameActive}
        inModal={inModal}
      />
      <S.ContentContainer>
        <ChatWatchlistManager
          isPending={isPending}
          getIsInWatchlist={getIsInWatchlist}
          onSelectItem={onSelectItem}
          onChatSubmit={onChatSubmit}
        />
        <TableWatchlistManager
          data={entitiesList}
          countEntitiesAdded={countEntitiesAdded}
          removeEntity={removeEntity}
          onRowOrderChange={onRowOrderChange}
        />
      </S.ContentContainer>
      <S.FooterContainer>
        {(errorKey || watchlistError) && (
          <>
            <S.StyledIcon
              size={20}
              iconName="ExclamationCircle"
              fillColor="var(--icon-critical)"
            />
            <S.ErrorMessage>
              {/* i18next-extract-mark-context-next-line ["duplicate", "emptyList", "emptyName", "serverError"]  */}
              {t('widget:watchlist.watchlistError', {
                context: errorKey,
              })}
            </S.ErrorMessage>
          </>
        )}
        <S.FlexContainer>
          {onCancel && (
            <Button
              label="Cancel"
              variant="tertiary"
              size="large"
              onClick={onCancel}
            />
          )}
          <Button
            label="Confirm"
            variant="primary"
            size="large"
            onClick={onConfirm}
            data-testid="watchlist-manager-confirm-button"
          />
        </S.FlexContainer>
      </S.FooterContainer>
      {blocker.state === 'blocked' && (
        <WatchlistTableModal
          modalType="cancel"
          listName={listName}
          onClose={() => blocker.reset()}
          exitEditMode={() => blocker.proceed()}
          saveChanges={saveAndExit}
          deleteWatchlist={() => {}}
        />
      )}
    </S.Container>
  );
};
